import { useState, useEffect, useRef, useCallback } from "react";

// Configurable via env vars — defaults to 30 min timeout, 5 min warning.
// NIST SP 800-63B AAL2 (password + MFA) specifies a 60-minute idle ceiling.
// VITE_INACTIVITY_TIMEOUT_MINUTES — total idle minutes before logout (default: 30)
// VITE_INACTIVITY_WARNING_MINUTES — minutes of warning before logout (default: 5)
const TIMEOUT_MS =
  (parseInt(import.meta.env.VITE_INACTIVITY_TIMEOUT_MINUTES ?? "") || 30) *
  60_000;
const WARNING_MS =
  (parseInt(import.meta.env.VITE_INACTIVITY_WARNING_MINUTES ?? "") || 5) *
  60_000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

// Throttle activity resets to once per 10 s to avoid churning timers on every
// mouse move, while still responding promptly to real user interaction.
const THROTTLE_MS = 10_000;

export function useInactivityTimeout(onTimeout: () => void) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(WARNING_MS);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastThrottleRef = useRef(0);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  });

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const startTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setRemainingMs(WARNING_MS);

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingMs(WARNING_MS);

      const warningEnd = Date.now() + WARNING_MS;
      countdownRef.current = setInterval(() => {
        setRemainingMs(Math.max(0, warningEnd - Date.now()));
      }, 500);
    }, TIMEOUT_MS - WARNING_MS);

    logoutTimerRef.current = setTimeout(() => {
      clearAllTimers();
      onTimeoutRef.current();
    }, TIMEOUT_MS);
  }, [clearAllTimers]);

  // Bypass throttle for explicit user action (e.g. "Stay signed in" button).
  const extendSession = useCallback(() => {
    lastThrottleRef.current = 0;
    startTimers();
  }, [startTimers]);

  useEffect(() => {
    startTimers();

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastThrottleRef.current < THROTTLE_MS) return;
      lastThrottleRef.current = now;
      startTimers();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true }),
    );

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [startTimers, clearAllTimers]);

  return { showWarning, remainingMs, extendSession };
}
